import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import EntityTable from "../../../../../common/Tables/EntityTable"
import { dateUtils } from "../../../../../utils/DateUtils";
import { Box } from "@mui/material";
import { ADJUSTMENT_SCOPES, ADJUSTMENT_TYPES, COMPARISON_OPERATORS, MODIFIER_TYPES } from "../../../../../common/enums";
import PriceModifiersEditor from "./PriceModifiersEditor";

const locale = navigator.language;

const getLOSConditionMessage = (los, comparisonOperator) => {
    switch (comparisonOperator) {
        case COMPARISON_OPERATORS.LESS_THAN:
            return `less than ${los} days`;
        case COMPARISON_OPERATORS.LESS_THAN_OR_EQUAL:
            return `${los} days or less`;
        case COMPARISON_OPERATORS.EQUAL:
            return `exactly ${los} days`;
        case COMPARISON_OPERATORS.GREATER_THAN_OR_EQUAL:
            return `${los} days or more`;
        case COMPARISON_OPERATORS.GREATER_THAN:
            return `more than ${los} days`;
        default:
            return null;
    }
}

const getNumberOfGuestsConditionMessage = (numberOfGuests, comparisonOperator) => {
    switch (comparisonOperator) {
        case COMPARISON_OPERATORS.LESS_THAN:
            return `less than ${numberOfGuests}`;
        case COMPARISON_OPERATORS.LESS_THAN_OR_EQUAL:
            return `${numberOfGuests} or less`;
        case COMPARISON_OPERATORS.EQUAL:
            return `exactly ${numberOfGuests}`;
        case COMPARISON_OPERATORS.GREATER_THAN_OR_EQUAL:
            return `${numberOfGuests} or more`;
        case COMPARISON_OPERATORS.GREATER_THAN:
            return `more than ${numberOfGuests}`;
        default:
            return null;
    }
}

const PriceModifiersList = ({ priceModifiers, rooms, plans, propertyId, onCreate, onUpdate, onDelete, deletePriceModifier, onError }) => {
    const columns = [
        {
            field: 'name',
            headerName: 'Name',
            minWidth: 200,
        }, {
            field: 'startDate',
            headerName: 'Start date',
            minWidth: 120,
            valueGetter: (params) => {
                if (!params.value) {
                    return '-';
                }
                const date = new Date(params.value);
                const dayOfWeek = date.toLocaleString(locale, { weekday: 'short' });
                return `${dayOfWeek} ${date.toLocaleDateString()}`;
            },
            align: 'center',
        }, {
            field: 'endDate',
            headerName: 'End date',
            minWidth: 120,
            valueGetter: (params) => {
                if (!params.value) {
                    return '-';
                }
                const date = new Date(params.value);
                const dayOfWeek = date.toLocaleString(locale, { weekday: 'short' });
                return `${dayOfWeek} ${date.toLocaleDateString()}`;
            },
            align: 'center',
        }, {
            field: 'condition',
            headerName: 'Condition',
            minWidth: 200,
            flex: 2,
            renderCell: (params) => {
                const { modifierType, conditionValue, comparisonOperator } = params.row;

                switch (modifierType) {
                    case MODIFIER_TYPES.ALL_THE_TIME:
                        return null;
                    case MODIFIER_TYPES.DAY_OF_WEEK:
                        const days = dateUtils.getDaysOfWeekListFromFlag(conditionValue);
                        return <Box>Day of week is: <strong>{`${days.map(day => dateUtils.getLocalizedDayName(day, locale)).join(', ')}`}</strong></Box>;
                    case MODIFIER_TYPES.LENGTH_OF_STAY:
                        return <Box>Length of stay is <strong>{getLOSConditionMessage(conditionValue, comparisonOperator)}</strong></Box>;
                    case MODIFIER_TYPES.NUMBER_OF_GUESTS:
                        return <Box>Occupancy is <strong>{getNumberOfGuestsConditionMessage(conditionValue, comparisonOperator)}</strong></Box>;
                    default:
                        return null;
                }
            }
        }, {
            field: 'adjustment',
            headerName: 'Price adjustment',
            minWidth: 200,
            flex: 2,
            renderCell: (params) => {
                const { adjustmentScope, adjustmentType, adjustmentValue } = params.row;

                const isIncrease = adjustmentValue > 0;
                const color = isIncrease ? 'success.main' : 'error.main';
                const scope = adjustmentScope === ADJUSTMENT_SCOPES.DAY ? 'day' : 'entire stay';
                const suffix = adjustmentType === ADJUSTMENT_TYPES.FIXED_AMOUNT ? 'zł' : '%';

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {isIncrease ? <ArrowUpward sx={{ color: 'success.main' }} /> : <ArrowDownward sx={{ color: 'error.main' }} />}
                        <Box component={'strong'} sx={{ color: color }}>{isIncrease ? '+' : ''}{adjustmentValue}{suffix}</Box>
                        <span>{`per`}</ span>
                        <strong>{`${scope}`}</strong>
                    </Box>
                );
            }
        }, {
            field: 'selectedPlanIds',
            headerName: 'Selected plans',
            flex: 1,
            minWidth: 120,
            renderCell: (params) => {
                const planIds = params.value;
                return (
                    <Box sx={{ whiteSpace: 'pre-line' }}>
                        {planIds
                            .map(id => {
                                const plan = plans.find(plan => plan.id === id);
                                return plan.name;
                            })
                            .join('\n')}
                    </Box>
                );
            }
        }, {
            field: 'selectedRoomIds',
            headerName: 'Selected rooms',
            flex: 1,
            minWidth: 120,
            renderCell: (params) => {
                const roomIds = params.value;
                return (
                    <Box sx={{ whiteSpace: 'pre' }}>
                        {roomIds
                            .map(id => {
                                const room = rooms.find(room => room.id === id);
                                return room.name;
                            })
                            .join('\n')}
                    </Box>
                );
            }
        }
    ];

    return <EntityTable
        data={priceModifiers}
        columns={columns}
        dataGridProps={{
            hideFooter: true,
            disableSelectionOnClick: true
        }}
        onEntityCreateSuccess={onCreate}
        onEntityUpdateSuccess={onUpdate}
        onEntityDeleteSuccess={onDelete}
        enableCreateEntity
        enableEditEntity
        enableDeleteEntity
        deleteEntityAction={deletePriceModifier}
        EditorComponent={PriceModifierEditorWrapper}
        editorProps={{
            propertyId: propertyId,
            availableRooms: rooms,
            availablePlans: plans
        }}
        onError={onError}
        noEntitiesMessage='There are no price modifiers available'
    />
}

const PriceModifierEditorWrapper = ({ editedEntity, existingEntities, ...props }) => {
    return <PriceModifiersEditor editedPriceModifier={editedEntity} existingPriceModifiers={existingEntities} {...props} />
}

export default PriceModifiersList;